import {
  Kpi as DomainKpi,
  StandardPeriod as DomainStandardPeriod,
  Granularity as DomainGranularity,
  KpiComparisonMode as DomainKpiComparisonMode,
} from "@acto/taskpane/core/domain";
import {
  Kpi as GqlKpi,
  StandardPeriod as GqlStandardPeriod,
  Granularity as GqlGranularity,
  KpiComparisonMode as GqlKpiComparisonMode,
} from "@acto/gql/graphql";

export function toGqlKpi(kpi: DomainKpi): GqlKpi {
  switch (kpi) {
    case DomainKpi.REVENUE:
      return GqlKpi.Revenue;
    case DomainKpi.PROFIT:
      return GqlKpi.Profit;
    case DomainKpi.ORDERS:
      return GqlKpi.Orders;
    case DomainKpi.MARGIN:
      return GqlKpi.Margin;
  }
}

export function toGqlPeriod(period: DomainStandardPeriod): GqlStandardPeriod {
  switch (period) {
    case DomainStandardPeriod.LAST_MONTH:
      return GqlStandardPeriod.LastMonth;
    case DomainStandardPeriod.LAST_3_MONTHS:
      return GqlStandardPeriod.Last_3Months;
    case DomainStandardPeriod.LAST_6_MONTHS:
      return GqlStandardPeriod.Last_6Months;
    case DomainStandardPeriod.LAST_12_MONTHS:
      return GqlStandardPeriod.Last_12Months;
    case DomainStandardPeriod.YEAR_TO_DATE:
      return GqlStandardPeriod.YearToDate;
  }
}

export function toGqlGranularity(granularity: DomainGranularity): GqlGranularity {
  switch (granularity) {
    case DomainGranularity.DAILY:
      return GqlGranularity.Daily;
    case DomainGranularity.WEEKLY:
      return GqlGranularity.Weekly;
    case DomainGranularity.MONTHLY:
      return GqlGranularity.Monthly;
    case DomainGranularity.QUARTERLY:
      return GqlGranularity.Quarterly;
    case DomainGranularity.YEARLY:
      return GqlGranularity.Yearly;
  }
}

export function toGqlKpiComparisonMode(mode: DomainKpiComparisonMode): GqlKpiComparisonMode {
  switch (mode) {
    case DomainKpiComparisonMode.PERIOD_OVER_PERIOD:
      return GqlKpiComparisonMode.PeriodOverPeriod;
    case DomainKpiComparisonMode.YEAR_OVER_YEAR:
      return GqlKpiComparisonMode.YearOverYear;
  }
}

export function fromGqlKpiComparisonMode(mode: GqlKpiComparisonMode): DomainKpiComparisonMode {
  switch (mode) {
    case GqlKpiComparisonMode.PeriodOverPeriod:
      return DomainKpiComparisonMode.PERIOD_OVER_PERIOD;
    case GqlKpiComparisonMode.YearOverYear:
      return DomainKpiComparisonMode.YEAR_OVER_YEAR;
  }
}

export function fromGqlKpi(kpi: GqlKpi): DomainKpi {
  switch (kpi) {
    case GqlKpi.Revenue:
      return DomainKpi.REVENUE;
    case GqlKpi.Profit:
      return DomainKpi.PROFIT;
    case GqlKpi.Orders:
      return DomainKpi.ORDERS;
    case GqlKpi.Margin:
      return DomainKpi.MARGIN;
    case GqlKpi.IncomingOrder:
      throw new Error("INCOMING_ORDER has no widget KPI");
  }
}

export function fromGqlPeriod(period: GqlStandardPeriod): DomainStandardPeriod {
  switch (period) {
    case GqlStandardPeriod.LastMonth:
      return DomainStandardPeriod.LAST_MONTH;
    case GqlStandardPeriod.Last_3Months:
      return DomainStandardPeriod.LAST_3_MONTHS;
    case GqlStandardPeriod.Last_6Months:
      return DomainStandardPeriod.LAST_6_MONTHS;
    case GqlStandardPeriod.Last_12Months:
      return DomainStandardPeriod.LAST_12_MONTHS;
    case GqlStandardPeriod.YearToDate:
      return DomainStandardPeriod.YEAR_TO_DATE;
  }
}

export function fromGqlGranularity(granularity: GqlGranularity): DomainGranularity {
  switch (granularity) {
    case GqlGranularity.Daily:
      return DomainGranularity.DAILY;
    case GqlGranularity.Weekly:
      return DomainGranularity.WEEKLY;
    case GqlGranularity.Monthly:
      return DomainGranularity.MONTHLY;
    case GqlGranularity.Quarterly:
      return DomainGranularity.QUARTERLY;
    case GqlGranularity.Yearly:
      return DomainGranularity.YEARLY;
  }
}

/**
 * Converts a PeriodOption to a GQL StandardPeriod.
 *
 * NOTE: THIS_WEEK, THIS_MONTH, and CUSTOM cannot be converted to StandardPeriod
 * because they require custom date ranges. These should be excluded from UI
 * components that use this function (see EXCLUDED_PERIODS in CustomersPage/TeamPage).
 *
 * @throws Error if an unsupported period is provided
 */
export function periodOptionToGqlPeriod(periodOption: string): GqlStandardPeriod {
  switch (periodOption) {
    case "LAST_MONTH":
      return GqlStandardPeriod.LastMonth;
    case "LAST_3_MONTHS":
      return GqlStandardPeriod.Last_3Months;
    case "LAST_6_MONTHS":
      return GqlStandardPeriod.Last_6Months;
    case "LAST_12_MONTHS":
      return GqlStandardPeriod.Last_12Months;
    case "THIS_WEEK":
    case "THIS_MONTH":
    case "CUSTOM":
      throw new Error(
        `Cannot convert ${periodOption} to GqlStandardPeriod. ` +
          `These periods require custom date ranges (DateRangeInput). ` +
          `Exclude them from UI using excludedPeriods prop.`,
      );
    default:
      throw new Error(`Unknown period option: ${periodOption}`);
  }
}
